import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import { Icon, Checkbox, Radio, Input, Select, Button, FormModel } from "ant-design-vue";
import { orderBy, toNumber } from "lodash";
import { fmtMsg, MessageHelper, NotificationType } from "commonui";
import moment from "moment";
import { Drawer } from "@/components/drawer";
import PageTitle from "@/components/page-title/page-title.vue";
import { GridRef } from "@/components/wijmo-grid/grid/grid";
import Grid from "@/components/wijmo-grid/grid/grid.vue";
import { CurriculumType, MethodologyTypeEnum } from "@/util/enum";
import {
	LessonPlanActivityAddEditModel,
	LessonPlanActivityCountForLPVForGridModel,
	LessonPlanActivityCountForLPVModel,
	LessonPlanActivityGridModel,
	LessonPlanActivityReferenceForLPVResponseModel,
	LessonPlanActivityResponseModel,
	LessonPlanActivitySaveEditModel
} from "@/services/lesson-plan-activities/model";
import {
	LPActivityValidations,
	LPVStatusToNameMap,
	MethodologyTypeItems,
	VerionGroupLicenseTypeToNameMap
} from "@/util/constants";
import { disabledDueToAccessLevel, getEndLevelOnLicenseType, getRouteAccessLevel, toTimeString } from "@/util/func";
import { getModule } from "vuex-module-decorators";
import TitleListState from "@/modules/title-list";
import { VCSLevelModel } from "@/models/title-list-model";
import { ILessonPlanActivitiesService, LessonPlanActivitiesService } from "@/services/lesson-plan-activities";
import { LPActivitiesLocale } from "@/locales/localeid";
import DurationSelect from "@/components/content-exposure/duration-select/duration-select.vue";
@Component({
	components: {
		Icon,
		PageTitle,
		Grid,
		Checkbox,
		Drawer,
		Radio,
		RadioGroup: Radio.Group,
		Form: FormModel,
		FormItem: FormModel.Item,
		Input,
		Select,
		Option: Select.Option,
		Button,
		DurationSelect
	}
})
export default class Activities extends Vue {
	@Ref() readonly gridCompRef!: GridRef;
	public titleListModule = getModule(TitleListState);
	lessonPlanActivitiesService: ILessonPlanActivitiesService = LessonPlanActivitiesService();

	public activityGridLoading = false;
	public isLittleSeedChecked: boolean = true;
	public isGrapeSeedChecked: boolean = true;

	public isAddActivity: boolean = false;
	public isEditActivity: boolean = false;
	public isReferencesDrawerOpen: boolean = false;
	public drawerTitle: string = "";
	public littleSeed: CurriculumType = CurriculumType.LittleSEED;
	public grapeSeed: CurriculumType = CurriculumType.GrapeSEED;
	public readonly allLanguageSkill: MethodologyTypeEnum = MethodologyTypeEnum.All;
	public startLevels: VCSLevelModel[] = [];
	public endLevels: VCSLevelModel[] = [];
	public activitiesList: LessonPlanActivityResponseModel[] = [];
	public actvitiesListForGrid: LessonPlanActivityGridModel[] = [];
	public enableSaveButton: boolean = false;
	public saveButtonLoading: boolean = false;
	public isDurationError: boolean = false;
	public activityLPVReferences: LessonPlanActivityReferenceForLPVResponseModel[] = [];
	public selectedActivityLPVId: number = 0;
	public referenceCountForLPV: LessonPlanActivityCountForLPVForGridModel[] = [];
	public referenceGridLoading: boolean = false;
	public selectedActivityId: number = 0;

	public addEditForm: LessonPlanActivityAddEditModel = {
		id: 0,
		name: "",
		curriculumType: CurriculumType.LittleSEED,
		minDuration: 0,
		maxDuration: 0,
		startUnit: 1,
		endUnit: 8,
		methodologies: [MethodologyTypeEnum.All]
	};

	mounted() {
		this.fetchLessonPlanActivities();
	}

	formatMessage(id: string, params?: any): string {
		return fmtMsg(id, params);
	}

	get lpActivitiesLocale(): any {
		return LPActivitiesLocale;
	}

	fetchLessonPlanActivities() {
		this.activityGridLoading = true;
		this.activitiesList = [];
		this.actvitiesListForGrid = [];
		let allLevels = orderBy(this.titleListModule.levels, ["curriculumType", "Unit"], ["desc", "asc"]);
		allLevels = allLevels.map((level, index) => {
			return {
				...level,
				sequence: index
			};
		});

		this.lessonPlanActivitiesService.getLessonPlanActivities().then((activities) => {
			activities.forEach((activity) => {
				const gridActivity: LessonPlanActivityGridModel = {
					id: activity.id,
					startUnitId: activity.startUnitId,
					endUnitId: activity.endUnitId,
					references: activity.references,
					duration: `${toTimeString(activity.minDuration)} - ${toTimeString(activity.maxDuration)}`,
					methodologies: activity.methodologies
						.map((methodologyId) => this.methodologyItems.get(methodologyId) || "")
						.join(", "),
					curriculumType: activity.curriculumType,
					licenseTypeName: VerionGroupLicenseTypeToNameMap[activity.curriculumType],
					name: activity.name,
					startUnitName:
						allLevels.find((level) => level.id === activity.startUnitId)?.displayName ||
						activity.startUnitId,
					endUnitName:
						allLevels.find((level) => level.id === activity.endUnitId)?.displayName || activity.endUnitId,
					startUnitSequence:
						allLevels.find((level) => level.id == activity.startUnitId)?.sequence ||
						allLevels.findIndex((level) => level.id === activity.startUnitId),
					endUnitSequence: allLevels.find((level) => level.id == activity.endUnitId)?.sequence || null
				};
				this.activitiesList.push(activity);
				this.actvitiesListForGrid.push(gridActivity);
			});
			this.actvitiesListForGrid = orderBy(this.actvitiesListForGrid, ["curriculumType"], ["desc"]);
			this.activityGridLoading = false;
		});
	}

	setEndLevels(startLevelId: number, endLevelId: number | null) {
		this.endLevels = getEndLevelOnLicenseType(this.titleListModule.levels, startLevelId);

		if (this.endLevels.length) {
			this.addEditForm.endUnit = endLevelId ? endLevelId : this.endLevels[0].id;
		} else {
			this.endLevels = [];
			if (this.addEditForm.curriculumType === CurriculumType.LittleSEED) {
				this.addEditForm.endUnit = endLevelId ? endLevelId : 1;
			} else {
				this.addEditForm.endUnit = endLevelId ? endLevelId : 40;
			}
		}
	}

	onAddActivity() {
		this.isAddActivity = true;
		this.drawerTitle = fmtMsg(LPActivitiesLocale.LPActivitiesDrawerAddActivitytitle);
		this.setLevelsOnAddEditLicenseType(null, null);
	}

	onEditActivity(activityId: number) {
		this.selectedActivityId = activityId;
		this.drawerTitle = fmtMsg(LPActivitiesLocale.LPActivitiesDrawerEditActivitytitle);
		this.lessonPlanActivitiesService.getLessonPlanActivity(activityId).then((activity) => {
			this.addEditForm = {
				id: activity.id,
				name: activity.name,
				curriculumType: activity.curriculumType,
				minDuration: activity.minDuration,
				maxDuration: activity.maxDuration,
				startUnit: activity.startUnitId,
				endUnit: activity.endUnitId,
				methodologies: [...activity.methodologies]
			};

			this.isEditActivity = true;
			this.setLevelsOnAddEditLicenseType(activity.startUnitId, activity.endUnitId);
		});
	}

	onRefrencesView() {
		this.drawerTitle = fmtMsg(LPActivitiesLocale.LPActivitiesDrawerReferencesActivitytitle);
	}

	onClose() {
		this.isAddActivity = false;
		this.isEditActivity = false;
		this.addEditForm = {
			id: 0,
			name: "",
			curriculumType: CurriculumType.LittleSEED,
			minDuration: 0,
			maxDuration: 0,
			startUnit: 1,
			endUnit: 8,
			methodologies: [MethodologyTypeEnum.All]
		};
		this.enableSaveButton = false;
		this.saveButtonLoading = false;
		this.referenceGridLoading = false;
		this.isReferencesDrawerOpen = false;
	}

	onAddEditLicenseTypeChange(e) {
		this.addEditForm.curriculumType = e.target.value;
		if (this.isAddActivity) {
			this.setLevelsOnAddEditLicenseType(null, null);
		}
	}

	get defaultMethodology(): number {
		return MethodologyTypeEnum.All;
	}

	onAddEditMethodologyChange(values: MethodologyTypeEnum[]): void {
		if (values.length > 1 && values.findIndex((v) => v === MethodologyTypeEnum.All) === 0) {
			this.addEditForm.methodologies = [...values].filter((v) => v !== MethodologyTypeEnum.All);
		} else if (values.length > 1 && !values.includes(MethodologyTypeEnum.All)) {
			this.addEditForm.methodologies = [...values].filter((v) => v !== MethodologyTypeEnum.All);
		} else if (values.length > 1 && values.includes(MethodologyTypeEnum.All)) {
			this.addEditForm.methodologies = [MethodologyTypeEnum.All];
		} else {
			this.addEditForm.methodologies = [...values];
		}
	}

	get methodologyItems(): Map<number, string> {
		return new Map([...MethodologyTypeItems]);
	}

	onAddEditStartUnitChange(value: number) {
		if (value > this.addEditForm.endUnit) {
			this.setEndLevels(value, null);
		} else {
			this.setEndLevels(value, this.addEditForm.endUnit);
		}
	}

	setLevelsOnAddEditLicenseType(startLevelId: number | null, endLevelId: number | null) {
		if (this.addEditForm.curriculumType === CurriculumType.LittleSEED) {
			this.startLevels = this.titleListModule.levels.filter(
				(level) => level.curriculumType === CurriculumType.LittleSEED
			);
		} else {
			this.startLevels = this.titleListModule.levels.filter(
				(level) => level.curriculumType === CurriculumType.GrapeSEED
			);
		}

		if (this.startLevels.length > 0) {
			this.addEditForm.startUnit = startLevelId ? startLevelId : this.startLevels[0].id;
			this.setEndLevels(this.addEditForm.startUnit, endLevelId);
		}
	}

	handleSave() {
		const form = this.$refs.addEditActivitiesForm as FormModel;
		form.validate((valid) => {
			if (valid) {
				this.saveButtonLoading = true;
				if (this.isAddActivity) {
					const saveActivity: LessonPlanActivitySaveEditModel = {
						name: this.addEditForm.name,
						maxDuration: this.addEditForm.maxDuration,
						minDuration: this.addEditForm.minDuration,
						startUnitId: this.addEditForm.startUnit,
						endUnitId: this.addEditForm.endUnit,
						methodologies: this.addEditForm.methodologies.includes(MethodologyTypeEnum.All)
							? [
									MethodologyTypeEnum.Classic,
									MethodologyTypeEnum.Digital,
									MethodologyTypeEnum.Independent,
									MethodologyTypeEnum.REP,
									MethodologyTypeEnum.Remote
							  ]
							: [...this.addEditForm.methodologies],
						curriculumType: this.addEditForm.curriculumType
					};

					this.lessonPlanActivitiesService
						.saveActivity(saveActivity)
						.then(() => {
							MessageHelper.Message(
								NotificationType.Success,
								fmtMsg(this.lpActivitiesLocale.LPActivitiesDrawerSaveActivitySuccessText)
							);
							this.onClose();
							this.fetchLessonPlanActivities();
						})
						.catch(() => {
							MessageHelper.Message(
								NotificationType.Failed,
								fmtMsg(this.lpActivitiesLocale.LPActivitiesDrawerSaveActivityErrorText)
							);
							this.onClose();
						});
				} else {
					const saveActivity: LessonPlanActivitySaveEditModel = {
						name: this.addEditForm.name,
						maxDuration: this.addEditForm.maxDuration,
						minDuration: this.addEditForm.minDuration,
						startUnitId: this.addEditForm.startUnit,
						endUnitId: this.addEditForm.endUnit,
						methodologies: this.addEditForm.methodologies.includes(MethodologyTypeEnum.All)
							? [
									MethodologyTypeEnum.Classic,
									MethodologyTypeEnum.Digital,
									MethodologyTypeEnum.Independent,
									MethodologyTypeEnum.REP,
									MethodologyTypeEnum.Remote
							  ]
							: [...this.addEditForm.methodologies],
						curriculumType: this.addEditForm.curriculumType
					};

					this.lessonPlanActivitiesService
						.editActivity(saveActivity, this.addEditForm.id)
						.then(() => {
							MessageHelper.Message(
								NotificationType.Success,
								fmtMsg(this.lpActivitiesLocale.LPActivitiesDrawerSaveActivitySuccessText)
							);
							this.onClose();
							this.fetchLessonPlanActivities();
						})
						.catch(() => {
							MessageHelper.Message(
								NotificationType.Failed,
								fmtMsg(this.lpActivitiesLocale.LPActivitiesDrawerSaveActivityErrorText)
							);
							this.onClose();
						});
				}
			}
		});
	}

	onAddEditStartDurationChange(value: string) {
		this.addEditForm.minDuration = 0;
		if (value) {
			const [mm, ss] = value.split(":");
			const minutes = toNumber(mm);
			const seconds = toNumber(ss);

			this.addEditForm.minDuration = minutes * 60 + seconds;
		}
	}

	onAddEditEndDurationChange(value) {
		this.addEditForm.maxDuration = 0;
		if (value) {
			const [mm, ss] = value.split(":");
			const minutes = toNumber(mm);
			const seconds = toNumber(ss);

			this.addEditForm.maxDuration = minutes * 60 + seconds;
		}
	}

	validateAddEditForm(item: string, isPassed: boolean, error: string) {
		if (isPassed != this.enableSaveButton) this.enableSaveButton = isPassed;
	}

	public addEditFormRules = {
		name: [
			{
				required: true,
				message: fmtMsg(this.lpActivitiesLocale.LPActivitiesDrawerNameRequiredText),
				trigger: "change"
			},
			{ validator: this.activityNameValidator, trigger: "change" }
		],
		methodologies: [
			{
				required: true,
				message: fmtMsg(this.lpActivitiesLocale.LPActivitiesDrawerMethodologiesRequiredText),
				trigger: "change"
			},
			{ validator: this.methodologyAndSaveValidator, trigger: "change" }
		]
	};

	activityNameValidator(rule: any, value: string, callback: (msg?: string) => void) {
		let activityNameExists: boolean = false;
		let activityNameExceedLimit: boolean = false;

		const activityListToSearch = this.activitiesList.filter((activity) => activity.id !== this.selectedActivityId);

		if (!value.trim().length) {
			callback(fmtMsg(this.lpActivitiesLocale.LPActivitiesDrawerNameRequiredText));
			this.enableSaveButton = false;
			return;
		}

		if (activityListToSearch.some((activity) => activity.name.toLocaleLowerCase() === value.toLocaleLowerCase())) {
			activityNameExists = true;
		}

		if (value.length > LPActivityValidations.nameCharacterLimit) {
			activityNameExceedLimit = true;
		}

		if (activityNameExists) {
			callback(fmtMsg(this.lpActivitiesLocale.LPActivitiesDrawerNameUniqueText));
			this.enableSaveButton = false;
			return;
		}

		if (activityNameExceedLimit) {
			callback(fmtMsg(this.lpActivitiesLocale.LPActivitiesDrawerNameCharactersExceedText));
			this.enableSaveButton = false;
			return;
		}

		const enableSaveButton = this.checkForSaveButtonEnableOrDisable(false, true, true);
		if (!enableSaveButton) {
			callback();
			this.enableSaveButton = enableSaveButton;
			return;
		}
		callback();
	}

	checkForSaveButtonEnableOrDisable(checkForName?: boolean, checkForDuration?: boolean, checkForMethodology?: boolean): boolean {
		if (checkForName) {
			const activityListToSearch = this.activitiesList.filter((activity) => activity.id !== this.selectedActivityId);

			if (!this.addEditForm.name.length) {
				return false;
			}

			if (activityListToSearch.some((activity) => activity.name.toLocaleLowerCase() === this.addEditForm.name.toLocaleLowerCase())) {
				return false;
			}

			if (this.addEditForm.name.length > LPActivityValidations.nameCharacterLimit) {
				return false;
			}
		}

		if (checkForMethodology) {
			if (!this.addEditForm.methodologies.length) {
				return false;
			}
		}

		if (checkForDuration) {
			if (this.isDurationError) {
				return false;
			}
		}
		return true;
	}
	methodologyAndSaveValidator(rule: any, value: string, callback: (msg?: string) => void) {
		if (!value.length || !this.checkForSaveButtonEnableOrDisable(true, true, false)) {
			callback();
			this.enableSaveButton = false;
			return;
		} else {
			callback();
			this.enableSaveButton = true;
			return;
		}
	}

	@Watch("addEditForm.minDuration", { deep: true })
	@Watch("addEditForm.maxDuration", { deep: true })
	showDurationError() {
		const isError = this.addEditForm.minDuration > this.addEditForm.maxDuration;
		if (isError) {
			this.enableSaveButton = false;
			this.isDurationError = true;
		} else {
			this.isDurationError = false;
			const enableSaveButton = this.checkForSaveButtonEnableOrDisable(true, false, true);
			if (!enableSaveButton) {
				this.enableSaveButton = enableSaveButton;
			}
			else {
				this.enableSaveButton = this.isAddActivity || this.isEditActivity;
			}
		}
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	duration(duration: number) {
		return toTimeString(duration);
	}

	onViewReferences(activityId: number) {
		this.selectedActivityId = activityId;
		this.fetchActivityLPVs(activityId);
	}

	onActivityLPVChange(lpvId: number) {
		const activityLPVReferencesDetail = this.activityLPVReferences.find(
			(lpRefernce) => lpRefernce.lessonPlanVersionId === lpvId
		)?.activityReferenceDetails;

		this.setActivityLPVReferences(activityLPVReferencesDetail);
	}

	fetchActivityLPVs(activityId: number) {
		this.referenceGridLoading = true;
		this.lessonPlanActivitiesService
			.getActivityReferenceCountForLPV(activityId)
			.then((lpvs) => {
				this.activityLPVReferences = lpvs.map((lpv) => {
					return {
						lessonPlanVersionId: lpv.lessonPlanVersionId,
						name: lpv.name,
						status: LPVStatusToNameMap[lpv.status] || "",
						activityReferenceDetails: lpv.activityReferenceDetails
					};
				});
				this.selectedActivityLPVId = lpvs[0].lessonPlanVersionId;
				this.setActivityLPVReferences(lpvs[0].activityReferenceDetails);

				this.drawerTitle = fmtMsg(this.lpActivitiesLocale.LPActivitiesDrawerReferencesTitle, {
					0: this.activitiesList.find((activity) => activity.id === this.selectedActivityId)?.name || ""
				});

				this.referenceGridLoading = false;
				this.isReferencesDrawerOpen = true;
			})
			.catch(() => {
				this.referenceGridLoading = false;
			});
	}

	setActivityLPVReferences(activityLPVReferencesDetail?: LessonPlanActivityCountForLPVModel[]) {
		if (activityLPVReferencesDetail) {
			this.referenceCountForLPV = activityLPVReferencesDetail.map((referenceDeail) => {
				return {
					...referenceDeail,
					unitName:
						this.titleListModule.levels.find((level) => level.id === referenceDeail.unitId)?.displayName ||
						""
				};
			});
		} else {
			this.referenceCountForLPV = [];
		}
	}
}
